import React from 'react';
import {Helmet} from 'react-helmet-async';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCheck,FiArrowRight,FiSearch,FiTrendingUp,FiFileText,FiLink,FiCalendar}=FiIcons;

const ConsulenzaSEO=()=> {
  const services=[
    {
      title: "Audit SEO Tecnico",
      description: "Analisi completa del sito per identificare e risolvere problemi tecnici",
      icon: FiSearch,
      features: ["Crawling e indicizzazione","Core Web Vitals","Schema markup"]
    },
    {
      title: "Ricerca Keywords",
      description: "Studio approfondito delle parole chiave per il tuo settore a Napoli",
      icon: FiFileText,
      features: ["Keyword locali","Analisi competitor","Search intent"]
    },
    {
      title: "SEO On-Page",
      description: "Ottimizzazione contenuti,meta tag e struttura per migliorare il ranking",
      icon: FiTrendingUp,
      features: ["Ottimizzazione contenuti","Meta tag","Internal linking"]
    },
    {
      title: "Link Building",
      description: "Strategia di acquisizione link di qualità per aumentare l'autorità",
      icon: FiLink,
      features: ["Link di qualità","Guest posting","Menzioni locali"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Consulenza SEO Napoli – Traffico Organico che Converte</title>
        <meta name="description" content="Consulenza SEO professionale a Napoli: audit tecnico,ottimizzazione contenuti,link building e SEO locale. Crescita organica stabile e duratura." />
        <meta name="keywords" content="consulenza seo napoli,seo tecnica napoli,posizionamento google napoli,seo locale napoli,link building napoli" />
        <link rel="canonical" href="https://realizzazionesitiweb-napoli.it/servizi/consulenza-seo-napoli" />
      </Helmet>

      {/* Hero Section - Velo scuro al 40% */}
      <section className="relative min-h-screen bg-napoli-gradient text-white overflow-hidden flex items-center">
        <div className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?q=80&w=1920&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              className="text-4xl lg:text-5xl font-poppins font-bold mb-6 text-white drop-shadow-lg"
            >
              Consulenza SEO Napoli
            </motion.h1>
            
            <motion.p
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: 0.2}}
              className="text-xl mb-8 text-white leading-relaxed drop-shadow-md"
            >
              Posizionamento organico su Google per PMI e professionisti di Napoli. Audit completo,strategia SEO su misura e crescita del traffico qualificato.
            </motion.p>
            
            <motion.div
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: 0.4}}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contatti"
                className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-accent focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
              >
                Preventivo gratuito in 24h
              </Link>
              <a
                href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-napoli-azzurro hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-azzurro focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" />
                Prenota una consulenza
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-napoli-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-napoli-blu mb-6">
              Servizi SEO completi
            </h2>
            <p className="text-xl text-napoli-blu font-medium">
              Dalla diagnosi all'implementazione,tutto per il tuo posizionamento
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: index * 0.1}}
                viewport={{once: true}}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-napoli-azzurro to-napoli-accent rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-napoli-blu" />
                </div>
                <h3 className="text-xl font-poppins font-semibold text-napoli-blu mb-3">
                  {service.title}
                </h3>
                <p className="text-napoli-blu mb-4 leading-relaxed font-medium">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature,idx)=> (
                    <li key={idx} className="flex items-center text-sm text-napoli-blu font-medium">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-napoli-accent mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Velo scuro al 40% */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?q=80&w=1920&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-poppins font-bold mb-6 text-white drop-shadow-lg">
            Vuoi essere primo su Google?
          </h2>
          <p className="text-xl mb-8 text-white max-w-2xl mx-auto font-medium drop-shadow-md">
            Audit SEO gratuito del tuo sito e strategia personalizzata per il tuo settore.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contatti"
              className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-accent focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
            >
              Preventivo gratuito in 24h
            </Link>
            <a
              href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-napoli-azzurro hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-azzurro focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" />
              Prenota una consulenza
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConsulenzaSEO;